import { Building2, ClipboardList, Cloud, CloudOff, FolderKanban, Globe2, Info, LayoutDashboard, Save, Settings, Shield, Sparkles, Swords, Ticket, Trophy, UsersRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from './auth';
import { useGameState, type GameState } from './gameState';
import { useMusicScene } from './music/MusicProvider';
import { AuthPage } from './pages/AuthPage';
import { ClubPage } from './pages/ClubPage';
import { CalendarPage } from './pages/CalendarPage';
import { CompetitionsPage } from './pages/CompetitionsPage';
import { DashboardPage } from './pages/DashboardPage';
import { GameMenuPage } from './pages/GameMenuPage';
import { InfoPage } from './pages/InfoPage';
import { MatchExperiencePage } from './pages/MatchExperiencePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OnlinePage } from './pages/OnlinePage';
import { PlayersPage } from './pages/PlayersPage';
import { RecruitmentPage } from './pages/RecruitmentPage';
import { RivalsPage } from './pages/RivalsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SeasonTransitionPage } from './pages/SeasonTransitionPage';
import { SquadPage } from './pages/SquadPage';
import { TacticsPage } from './pages/TacticsPage';
import { AffinityGamesCallupModal } from './components/AffinityGamesCallupModal';
import { persistGameSave, type GameSaveSlot } from './services/gameSaves';
import { readSaveMode, SAVE_MODE_KEY, type SaveMode } from './saveMode';

const navigation = [
  { id: 'dashboard', label: 'Portal', icon: LayoutDashboard },
  { id: 'squad', label: 'Plantilla', icon: ClipboardList },
  { id: 'players', label: 'Jugadores', icon: UsersRound },
  { id: 'tactics', label: 'Tácticas', icon: Swords },
  { id: 'rivals', label: 'Equipos', icon: Shield },
  { id: 'competitions', label: 'Competiciones', icon: Trophy },
  { id: 'online', label: 'Online', icon: Globe2 },
  { id: 'recruitment', label: 'Reclutamiento', icon: Ticket },
  { id: 'club', label: 'Club', icon: Building2 },
];

const managerPages = new Set(['dashboard', 'squad', 'players', 'tactics', 'rivals', 'competitions', 'online', 'recruitment', 'club', 'info', 'settings', 'calendar']);
const managerRoutes = new Set([...managerPages, 'match']);

function pageFromLocation() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/manager') return 'dashboard';
  const section = path.match(/^\/manager\/([^/]+)$/)?.[1];
  return section && managerRoutes.has(section) ? section : 'dashboard';
}

function hasKnownPath() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  // Query-string page routes were a temporary implementation detail. Keeping
  // them would make `/` and `/?page=squad` silently represent different UI
  // states, so URL parameters now deliberately lead to the 404 fallback.
  if (window.location.search) return false;
  const section = path.match(/^\/manager\/([^/]+)$/)?.[1];
  return path === '/' || path === '/manager' || Boolean(section && managerRoutes.has(section));
}

function pathForPage(page: string) {
  return page === 'dashboard' ? '/manager' : `/manager/${page}`;
}

function ActiveGame({ slot, managerName, onExit }: { slot: GameSaveSlot; managerName: string; onExit(): void }) {
  const game = useGameState(slot.saveData, `inazuma-manager-slot-${slot.id}`, slot.updatedAt);
  const [saveMode, setSaveMode] = useState<SaveMode>(readSaveMode);
  const [cloudState, setCloudState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const firstSnapshot = useRef(true);
  // If a train journey/offline session produced a newer local snapshot, write
  // it back as soon as connectivity returns instead of restoring an older
  // cloud copy and losing recruited players or spent tickets.
  const needsInitialPersistence = useRef(slot.saveData.version !== game.save.version || game.recoveredFromLocal);
  const latestSave = useRef(game.save);
  const saveModeRef = useRef(saveMode);
  const revision = useRef(0);
  const dirty = useRef(false);
  const autoSaveTimer = useRef<number | null>(null);
  const feedbackTimer = useRef<number | null>(null);

  const clearAutoSaveTimer = () => {
    if (autoSaveTimer.current !== null) window.clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = null;
  };
  const clearFeedbackTimer = () => {
    if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current);
    feedbackTimer.current = null;
  };
  const saveCurrent = (withFeedback: boolean) => {
    clearAutoSaveTimer();
    clearFeedbackTimer();
    const snapshot = latestSave.current;
    const snapshotRevision = revision.current;
    if (withFeedback) setCloudState('saving');
    void persistGameSave(slot.id, snapshot)
      .then(() => {
        if (snapshotRevision === revision.current) dirty.current = false;
        if (!withFeedback) return;
        setCloudState('saved');
        feedbackTimer.current = window.setTimeout(() => setCloudState('idle'), 5000);
      })
      .catch(() => {
        if (withFeedback) setCloudState('error');
      });
  };
  const scheduleAutoSave = () => {
    clearAutoSaveTimer();
    autoSaveTimer.current = window.setTimeout(() => saveCurrent(false), 650);
  };

  useEffect(() => {
    latestSave.current = game.save;
    if (firstSnapshot.current) {
      firstSnapshot.current = false;
      if (!needsInitialPersistence.current) return;
      revision.current += 1;
      dirty.current = true;
      if (saveModeRef.current === 'auto') scheduleAutoSave();
      return;
    }
    revision.current += 1;
    dirty.current = true;
    if (saveModeRef.current === 'auto') scheduleAutoSave();
    else setCloudState('idle');
  }, [game.save]);

  useEffect(() => () => { clearAutoSaveTimer(); clearFeedbackTimer(); }, []);

  const changeSaveMode = (mode: SaveMode) => {
    saveModeRef.current = mode;
    setSaveMode(mode);
    localStorage.setItem(SAVE_MODE_KEY, mode);
    clearFeedbackTimer();
    setCloudState('idle');
    if (mode === 'auto' && dirty.current) scheduleAutoSave();
    if (mode === 'manual') clearAutoSaveTimer();
  };

  return <ManagerApp game={game} gameSaveId={slot.id} managerName={managerName} saveMode={saveMode} cloudState={cloudState} onManualSave={() => saveCurrent(true)} onSaveModeChange={changeSaveMode} onExit={onExit} />;
}

function ManagerApp({ game, gameSaveId, managerName, saveMode, cloudState, onManualSave, onSaveModeChange, onExit }: { game: GameState; gameSaveId: string; managerName: string; saveMode: SaveMode; cloudState: 'idle' | 'saving' | 'saved' | 'error'; onManualSave(): void; onSaveModeChange(mode: SaveMode): void; onExit(): void }) {
  const [page, setPage] = useState(pageFromLocation);
  const [spiritTargetId, setSpiritTargetId] = useState<string | undefined>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('inazuma-sidebar-collapsed') === '1');
  useMusicScene(page === 'match' ? undefined : 'menu');

  useEffect(() => { localStorage.setItem('inazuma-sidebar-collapsed', sidebarCollapsed ? '1' : '0'); }, [sidebarCollapsed]);
  const navigate = (next: string) => {
    setSpiritTargetId(undefined);
    setPage(next);
    window.history.replaceState(null, '', pathForPage(next));
  };
  const openSpiritSanctuary = (playerId: string) => {
    setSpiritTargetId(playerId);
    setPage('club');
    window.history.replaceState(null, '', pathForPage('club'));
  };
  if (!managerPages.has(page) && page !== 'match') return <NotFoundPage />;
  if (page === 'match') return <MatchExperiencePage game={game} onExit={() => navigate('dashboard')} />;
  if (game.save.seasonReview) return <SeasonTransitionPage game={game} review={game.save.seasonReview} onContinue={() => { game.dismissSeasonReview(); navigate('dashboard'); }} />;
  const toggleSidebarFromLogo = () => setSidebarCollapsed((value) => !value);
  const currentSection = page === 'info' ? 'Información' : page === 'settings' ? 'Ajustes' : page === 'calendar' ? 'Calendario' : navigation.find((item) => item.id === page)?.label ?? 'Portal';
  const tokenCount = Object.values(game.save.tokens).reduce((sum, value) => sum + value, 0);
  const affinityCallupDue = Boolean(game.save.affinityGames
    && !game.save.affinityGames.presentationComplete
    && game.save.careerDate >= game.save.affinityGames.announcementDate);

  return <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
    <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="brand"><button className="brand-toggle" onClick={toggleSidebarFromLogo} aria-label="Pasar la navegación a la barra superior" title="Pasar la navegación a la barra superior"><img className="brand-logo" src="/inazuma_manager_eleven.png" alt="Inazuma Manager Eleven" /></button></div>
      <nav className="primary-nav"><span className="nav-caption">NAVEGACIÓN</span>{navigation.map((item) => { const Icon = item.icon; return <button className={page === item.id ? 'active' : ''} onClick={() => navigate(item.id)} key={item.id}><Icon size={19} strokeWidth={1.8} /><span>{item.label}</span>{item.id === 'recruitment' && <em>{tokenCount}</em>}</button>; })}</nav>
      <div className="sidebar-spacer" />
      <div className="season-progress"><div><span><Sparkles size={14} /> PROGRESO</span><b>62%</b></div><div className="season-bar"><i /></div><small>Temporada de iniciación</small></div>
      <nav className="secondary-nav"><button className={page === 'info' ? 'active' : ''} onClick={() => navigate('info')}><Info size={18} /> Información</button><button className={page === 'settings' ? 'active' : ''} onClick={() => navigate('settings')}><Settings size={18} /> Ajustes</button></nav>
      <div className="manager-profile"><span className="manager-avatar">{managerName.slice(0, 2).toUpperCase()}</span><span><b>{managerName}</b><small>{game.save.clubName}</small></span><button onClick={onExit} title="Cambiar de partida"><FolderKanban size={16} /></button></div>
    </aside>
    <div className="app-main">
      <header className="topbar"><div className="topbar-navigation-wrap"><button className="topbar-brand-toggle" onClick={toggleSidebarFromLogo} aria-label="Volver a la navegación lateral" title="Volver a la navegación lateral"><img src="/inazuma_manager_eleven.png" alt="Inazuma Manager Eleven" /></button><nav className="topbar-horizontal-nav" aria-label="Navegación principal">{navigation.map((item) => { const Icon = item.icon; return <button className={page === item.id ? 'active' : ''} onClick={() => navigate(item.id)} title={item.label} key={item.id}><Icon size={17} strokeWidth={1.8} /><span>{item.label}</span>{item.id === 'recruitment' && <em>{tokenCount}</em>}</button>; })}</nav><span className="topbar-section"><small>SECCIÓN</small><b>{currentSection}</b></span></div><div className="topbar-actions">{saveMode === 'manual' && (cloudState === 'saved' ? <span className="cloud-status saved"><Cloud size={15} /> Guardado</span> : <button className={`manual-save-button ${cloudState === 'error' ? 'error' : ''}`} onClick={onManualSave} disabled={cloudState === 'saving'}>{cloudState === 'saving' ? <Cloud size={15} /> : cloudState === 'error' ? <CloudOff size={15} /> : <Save size={15} />}{cloudState === 'saving' ? 'Guardando' : cloudState === 'error' ? 'Reintentar guardado' : 'Guardar partida'}</button>)}<button className="quick-match" onClick={() => setPage('match')}>JUGAR PARTIDO <span>→</span></button></div></header>
      <div className="content-area">{page === 'dashboard' && <DashboardPage game={game} onNavigate={navigate} />}{page === 'calendar' && <CalendarPage game={game} onPlay={() => setPage('match')} />}{page === 'squad' && <SquadPage game={game} onImprove={openSpiritSanctuary} />}{page === 'players' && <PlayersPage />}{page === 'tactics' && <TacticsPage game={game} />}{page === 'rivals' && <RivalsPage game={game} />}{page === 'competitions' && <CompetitionsPage game={game} />}{page === 'online' && <OnlinePage gameSaveId={gameSaveId} managerName={managerName} />}{page === 'recruitment' && <RecruitmentPage game={game} />}{page === 'club' && <ClubPage game={game} saveMode={saveMode} initialSpiritTargetId={spiritTargetId} />}{page === 'info' && <InfoPage />}{page === 'settings' && <SettingsPage sidebarCollapsed={sidebarCollapsed} onNavigationLayoutChange={setSidebarCollapsed} saveMode={saveMode} onSaveModeChange={onSaveModeChange} />}</div>
    </div>
    {affinityCallupDue && <AffinityGamesCallupModal game={game} />}
  </div>;
}

function Gate() {
  const { user, loading, signOut } = useAuth();
  const [slot, setSlot] = useState<GameSaveSlot | null>(null);
  useEffect(() => { setSlot(null); }, [user?.id]);
  if (loading) return <main className="account-loading">Conectando con tu cuenta…</main>;
  if (!user) return <AuthPage />;
  const managerName = String(user.user_metadata.display_name ?? user.email?.split('@')[0] ?? 'Entrenador');
  return slot ? <ActiveGame key={slot.id} slot={slot} managerName={managerName} onExit={() => { window.history.replaceState(null, '', '/'); setSlot(null); }} /> : <GameMenuPage user={user} onOpen={(nextSlot) => {
    // Opening a slot from the landing page enters the manager dashboard;
    // opening one from a bookmarked manager subsection preserves that route.
    if (window.location.pathname === '/') window.history.replaceState(null, '', '/manager');
    setSlot(nextSlot);
  }} onSignOut={() => void signOut()} />;
}

export default function App() {
  if (!hasKnownPath()) return <NotFoundPage />;
  return <AuthProvider><Gate /></AuthProvider>;
}
